import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PaymentMethod, PaymentPlanFrequency } from '@frontend/api-payment-plans';
import { Merchant } from '@frontend/api-payments';
import { RelativeContainer } from '../components/payment-methods/containers/relative-container';
import { VerticalGrid } from '../components/payment-methods/containers/vertical-grid';
import { PaymentMethodsNoGatewayPlaceholder } from '../components/payment-methods/placeholders/payment-methods-placeholder';
import { PlatformComponentProvider } from '../contexts/platform-component.context';
import { ProcessorDataProvider } from '../contexts/processor-data.context';
import { CardOnFileDefaultActions } from './card-on-file/default/default-actions';
import { CardOnFileDefaultForm } from './card-on-file/default/default-form';
import {
  PaymentMethodsViewSwitch,
  PaymentMethodsViewSwitchProps,
} from './payment-methods/atoms/payment-methods-view-switch';
import { PaymentMethodsProvider } from './payment-methods/context/payment-methods.context';
import { ContextToProps } from './payment-methods/context-to-props';
import { PaymentMethodsDefaultAchActions } from './payment-methods/default/default-ach-actions';
import { PaymentMethodsDefaultAchForm } from './payment-methods/default/default-ach-form';
import { DefaultActionsFooter } from './payment-methods/default/default-actions-footer';
import { PaymentMethodsDefaultList } from './payment-methods/default/default-list';
import { PaymentMethodsDefaultListActions } from './payment-methods/default/default-list-actions';

export type PaymentMethodsProps = React.PropsWithChildren<{
  onPaymentMethodSelectedSuccess: (paymentMethod: PaymentMethod) => void;
  onVerificationEmailSent?: () => void;
  onError?: (message: string, err?: Error) => void;
  onCancel?: () => void;
  locationId: string;
  personId?: string;
  person?: Person;
  invoiceId?: string;
  isPublic?: boolean;
  isRegularLocation?: boolean;
  amount?: number;
  merchant?: Merchant;
  noGatewayPlaceholder?: React.ReactNode;
  showCancelButtonOnList?: boolean;
  frequency?: {
    oneTimePayment?: boolean;
    paymentFrequency?: PaymentPlanFrequency;
  };
}>;

type PaymentMethodsNamespace = React.FC<PaymentMethodsProps> & {
  View: React.FC<PaymentMethodsViewSwitchProps>;
  ContextToProps: typeof ContextToProps;
  List: typeof PaymentMethodsDefaultList;
  ListActions: typeof PaymentMethodsDefaultListActions;
  AddCardForm: typeof CardOnFileDefaultForm;
  CardActions: typeof CardOnFileDefaultActions;
  AchForm: typeof PaymentMethodsDefaultAchForm;
  AchActions: typeof PaymentMethodsDefaultAchActions;
  FooterActions: typeof DefaultActionsFooter;
  VerticalGrid: typeof VerticalGrid;
  RelativeContainer: typeof RelativeContainer;
  NoGatewayPlaceholder: typeof PaymentMethodsNoGatewayPlaceholder;
};

const PaymentMethods: PaymentMethodsNamespace = ({
  children,
  locationId,
  isPublic = false,
  isRegularLocation = true,
  merchant,
  ...props
}: PaymentMethodsProps) => {
  return (
    <PlatformComponentProvider locationId={locationId} isPublic={isPublic} isRegularLocation={isRegularLocation}>
      <ProcessorDataProvider merchant={merchant}>
        <PaymentMethodsProvider {...props}>{children}</PaymentMethodsProvider>
      </ProcessorDataProvider>
    </PlatformComponentProvider>
  );
};

PaymentMethods.View = PaymentMethodsViewSwitch;
PaymentMethods.ContextToProps = ContextToProps; // Placeholder for actual context to props logic
PaymentMethods.List = PaymentMethodsDefaultList;
PaymentMethods.ListActions = PaymentMethodsDefaultListActions;
PaymentMethods.AddCardForm = CardOnFileDefaultForm;
PaymentMethods.CardActions = CardOnFileDefaultActions;
PaymentMethods.AchForm = PaymentMethodsDefaultAchForm;
PaymentMethods.AchActions = PaymentMethodsDefaultAchActions;
PaymentMethods.VerticalGrid = VerticalGrid;
PaymentMethods.RelativeContainer = RelativeContainer;
PaymentMethods.FooterActions = DefaultActionsFooter;
PaymentMethods.NoGatewayPlaceholder = PaymentMethodsNoGatewayPlaceholder;

export { PaymentMethods };
